/**
 * Upload Center - Entity-First Content Ingestion
 *
 * Composes existing components:
 * - EntityPicker (from GET /document-entities) — BLOCKER gate
 * - DocumentUploadZone (file upload + URL extract tabs) — CS11 pipeline
 * - PasteURLTab (single URL + domain crawl) — URL extraction
 * - Persistent documents table grouped by entity
 *
 * Flow: Select Entity → Upload/Extract → Documents Table
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Wrench,
  Users,
  Lightbulb,
  Upload,
  FileText,
  Folder,
  FolderOpen,
  Link,
  Trash2,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Shield
} from 'lucide-react';
import { apiFetch, apiRequest } from '../lib/api';
import { getOrganizationIdFromStorage } from '../utils/authHeaders';
import { DocumentUploadZone } from './workflow/DocumentUploadZone';
import { PasteURLTab } from './workflow/steps/PasteURLTab';
// ─── Document Type Labels (matches backend DocumentType enum) ────────────

const ASSET_TYPE_LABELS: Record<string, string> = {
  company_profile: 'Company Profile',
  vendor_profile: 'Vendor Profile',
  partner_profile: 'Partner Profile',
  solution_brief: 'Campaign Brief',
  datasheet: 'Datasheet',
  product_comparison: 'Product Comparison',
  pricing_sheet: 'Pricing Sheet',
  integration_guide: 'Integration Guide',
  guide: 'Guide',
  case_study: 'Case Study',
  testimonial: 'Testimonial',
  reference_sheet: 'Reference Sheet',
  icp_definition: 'ICP Definition',
  market_analysis: 'Market Analysis',
  buyer_persona: 'Buyer Persona',
  buying_committee: 'Buying Committee',
  value_proposition: 'Value Proposition',
  messaging_guide: 'Messaging Guide',
  brand_guidelines: 'Brand Guidelines',
  competitive_intel: 'Competitive Intel',
  campaign_brief: 'Campaign Brief',
  email_template: 'Email Template',
  landing_page: 'Landing Page',
  social_content: 'Social Content',
  presentation: 'Presentation',
  one_pager: 'One-Pager',
  whitepaper: 'Whitepaper',
  blog_post: 'Blog Post',
  partner_case_study: 'Partner Case Study',
  partner_questionnaire: 'Partner Questionnaire',
  mdf_application: 'MDF Application',
  general_document: 'General Document',
};
// ─── Types ───────────────────────────────────────────────────

interface EntityOption {
  id: string;
  name: string;
  type: 'client' | 'vendor' | 'partner' | 'solution' | 'governance';
  parent?: string;
  icon?: string;
}

interface DocumentRecord {
  id: string;
  name: string;
  size: number;
  type: string;
  status: string;
  documentType?: string;
  stepName?: string;
  uploadedAt: string;
  entity?: { id: string; name: string; type: string } | null;
  metadata?: {
    format_type?: string;
    format_confidence?: number;
    purpose_type?: string;
    primary_audience_level?: string;
    audience_domain?: string;
    industry_type?: string;
    buyer_stage?: string;
    document_category?: string;
    technical_depth?: string;
    entity_type?: string;
    subject_discipline?: string;
    source?: string;
    processing_time_ms?: number;
  };
}

// ─── Component ───────────────────────────────────────────────

export const UploadCenter: React.FC = () => {
  const navigate = useNavigate();

  // Entity state
  const [entities, setEntities] = useState<EntityOption[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(null);
  const [loadingEntities, setLoadingEntities] = useState(true);

  // Content mode: file upload vs URL extraction vs text paste
  const [contentMode, setContentMode] = useState<'upload' | 'url' | 'paste'>('upload');

  // Documents list
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  // Template marking state
  const [markingTemplate, setMarkingTemplate] = useState<Record<string, boolean>>({});

  // ─── Data Loading ─────────────────────────────────────────

  const loadEntities = useCallback(async () => {
    try {
      setLoadingEntities(true);
      const oid = getOrganizationIdFromStorage();
      const q = oid ? `?org_id=${encodeURIComponent(oid)}` : '';
      const response = await apiFetch(`/api/v1/document-entities${q}`);
      const apiEntities = response?.entities || [];
      // Inject synthetic governance entity (not from DB — used for governance doc uploads)
      const governanceEntity: EntityOption = {
        id: 'governance',
        name: 'Governance Documents',
        type: 'governance',
      };
      setEntities([...apiEntities, governanceEntity]);
    } catch (error) {
      console.error('Failed to load entities:', error);
      setEntities([]);
    } finally {
      setLoadingEntities(false);
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      setLoadingDocs(true);
      const oid = getOrganizationIdFromStorage();
      const q = oid ? `?org_id=${encodeURIComponent(oid)}` : '';
      const response = await apiFetch(`/api/v1/documents${q}`);
      const filesData = Array.isArray(response)
        ? response
        : (response?.data?.documents || response?.documents || []);
      const docs: DocumentRecord[] = (Array.isArray(filesData) ? filesData : []).map((f: any) => ({
        id: f.id,
        name: f.name || f.filename || 'Unknown',
        size: f.file_size || f.size || 0,
        type: f.file_type || f.type || 'unknown',
        status: f.processing_status || f.status || 'pending',
        documentType: f.asset_type || f.documentType || null,
        stepName: f.stepName,
        uploadedAt: f.uploadedAt || f.created_at || new Date().toISOString(),
        entity: f.entity || null,
        metadata: f.metadata || null, // LLM classification metadata (9 facets)
      }));
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    loadEntities();
    loadDocuments();
  }, [loadEntities, loadDocuments]);

  // ─── Entity → FK prop mapping ─────────────────────────────

  const entityFKProps = selectedEntity
    ? {
      clientOrganizationId: selectedEntity.type === 'client' ? selectedEntity.id : undefined,
      clientPartnerId: selectedEntity.type === 'partner' ? selectedEntity.id : undefined,
      clientVendorId: selectedEntity.type === 'vendor' ? selectedEntity.id : undefined,
      vendorSolutionId: selectedEntity.type === 'solution' ? selectedEntity.id : undefined,
    }
    : {};

  const orgId = getOrganizationIdFromStorage() ?? '';

  // ─── Handlers ─────────────────────────────────────────────

  const handleExtractionComplete = useCallback(
    (_data: Record<string, unknown>, _citations: Array<unknown>) => {
      setTimeout(() => loadDocuments(), 1500);
    },
    [loadDocuments],
  );

  const handleUrlContentAdded = useCallback(
    (_docId: string, _title: string, _preview: string) => {
      setTimeout(() => loadDocuments(), 1500);
    },
    [loadDocuments],
  );

  const handleDeleteDocument = async (docId: string, docName: string) => {
    if (!confirm(`Delete "${docName}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/v1/documents/${docId}`, { method: 'DELETE' });
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  // Extract rich description from LLM metadata
  const getRichDescription = (doc: DocumentRecord): string => {
    const meta = doc.metadata;

    if (meta && meta.format_type && meta.source === 'openai_gpt4_mini') {
      const parts: string[] = [];

      // Primary classification
      if (meta.format_type) {
        parts.push(ASSET_TYPE_LABELS[meta.format_type] || meta.format_type);
      }

      // Audience + industry context
      const context: string[] = [];
      if (meta.primary_audience_level) context.push(`${meta.primary_audience_level} level`);
      if (meta.industry_type && meta.industry_type !== 'cross_industry') context.push(meta.industry_type);
      if (context.length > 0) parts.push(`for ${context.join(', ')}`);

      // Buyer stage
      if (meta.buyer_stage) parts.push(`(${meta.buyer_stage} stage)`);

      return parts.join(' ');
    }

    // Fallback to legacy description
    return getAIDescription(doc.documentType, doc.name);
  };

  // AI-powered description generator (lightweight - no RAG calls)
  const getAIDescription = (docType: string | undefined, filename: string): string => {
    // For unclassified or "other" type, infer from filename
    if (!docType || docType === 'general_document' || docType === 'other') {
      const lower = filename.toLowerCase();

      // Content type inference from filename
      if (lower.includes('transcript') || lower.includes('kick off')) return 'Meeting transcript and discussion notes';
      if (lower.includes('questionnaire') || lower.includes('survey')) return 'Survey responses and partner questionnaire';
      if (lower.includes('marketscape') || lower.includes('idc') || lower.includes('analyst')) return 'Industry analyst report and market research';
      if (lower.includes('introduc')) return 'Product introduction and overview materials';
      if (lower.includes('price') || lower.includes('pricing')) return 'Pricing and cost information';
      if (lower.includes('case') || lower.includes('study')) return 'Customer success story and use cases';
      if (lower.includes('brief') || lower.includes('summary')) return 'Executive summary and strategic brief';
      if (lower.includes('guide')) return 'Implementation guide and best practices';
      if (lower.includes('data') || lower.includes('spec')) return 'Technical specifications and datasheets';

      return 'Business content for campaign strategy';
    }

    // Smart descriptions based on document type
    const descriptions: Record<string, string> = {
      company_profile: 'Customer organization overview and background',
      vendor_profile: 'Technology vendor capabilities and offerings',
      partner_profile: 'Partner ecosystem and collaboration details',
      solution_brief: 'Product features, benefits, and use cases (Campaign Brief)',
      datasheet: 'Technical specifications and product details',
      product_comparison: 'Competitive analysis and feature comparison',
      pricing_sheet: 'Pricing models, tiers, and cost breakdown',
      integration_guide: 'Technical integration documentation',
      guide: 'Implementation guide and best practices',
      case_study: 'Customer success story and ROI metrics',
      testimonial: 'Customer feedback and endorsements',
      reference_sheet: 'Customer references and proof points',
      icp_definition: 'Ideal customer profile and targeting criteria',
      market_analysis: 'Market trends, sizing, and landscape',
      buyer_persona: 'Target buyer characteristics and pain points',
      buying_committee: 'Stakeholder roles and influence mapping',
      value_proposition: 'Unique value and differentiation messaging',
      messaging_guide: 'Brand voice and positioning framework',
      brand_guidelines: 'Visual identity and style standards',
      competitive_intel: 'Competitor analysis and market positioning',
      campaign_brief: 'Campaign objectives and creative direction',
      email_template: 'Email copy and engagement template',
      landing_page: 'Conversion-focused web page content',
      social_content: 'Social media posts and engagement content',
      presentation: 'Slide deck for presentations',
      one_pager: 'Single-page overview and summary',
      whitepaper: 'In-depth thought leadership content',
      blog_post: 'Educational or promotional article',
      partner_case_study: 'Partner collaboration success story',
      partner_questionnaire: 'Partner assessment and qualification form',
      mdf_application: 'Market development fund request document',
    };

    return descriptions[docType] || 'Strategic content for campaigns';
  };

  // ─── Helpers ──────────────────────────────────────────────

  const getEntityIcon = (type: string) => {
    const iconProps = { size: 16, className: "flex-shrink-0" };
    switch (type) {
      case 'client': return <Building2 {...iconProps} />;
      case 'vendor': return <Wrench {...iconProps} />;
      case 'partner': return <Users {...iconProps} />;
      case 'solution': return <Lightbulb {...iconProps} />;
      case 'governance': return <Shield {...iconProps} />;
      default: return <Folder {...iconProps} />;
    }
  };

  const statusBadge = (status: string) => {
    let effective = status;
    if (effective.includes('processed') || effective.includes('complete') || effective.includes('indexed')) effective = 'completed';
    else if (effective.includes('fail') || effective.includes('error')) effective = 'error';
    else if (effective.includes('process')) effective = 'processing';
    const cfg: Record<string, { color: string; label: string; icon: string }> = {
      completed: {
        color: 'text-emerald-800 bg-emerald-50 border-emerald-200',
        label: 'Processed',
        icon: '✅',
      },
      processing: {
        color: 'text-yellow-700 bg-yellow-50 border-yellow-200',
        label: 'Processing',
        icon: '🔄',
      },
      error: {
        color: 'text-red-700 bg-red-50 border-red-200',
        label: 'Failed',
        icon: '❌',
      },
    };
    const c = cfg[effective] || {
      color: 'text-gray-700 bg-gray-50 border-gray-200',
      label: 'Pending',
      icon: '⏳',
    };
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full border ${c.color}`}
      >
        {c.icon} {c.label}
      </span>
    );
  };

  const formatSize = (bytes: number) => {
    if (!bytes) return '—';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return (
        d.toLocaleDateString() +
        ' ' +
        d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    } catch {
      return '—';
    }
  };

  // ─── Group documents by entity ────────────────────────────

  const grouped = documents.reduce<Record<string, DocumentRecord[]>>((acc, doc) => {
    let key: string;
    if (doc.entity) {
      key = `${doc.entity.type}:${doc.entity.id}`;
    } else if (doc.metadata?.document_category === 'governance') {
      key = 'governance:governance';
    } else {
      key = 'unassigned';
    }
    if (!acc[key]) acc[key] = [];
    acc[key].push(doc);
    return acc;
  }, {});

  const groupKeys = Object.keys(grouped).sort((a, b) => {
    if (a === 'unassigned') return 1;
    if (b === 'unassigned') return -1;
    return a.localeCompare(b);
  });

  const entityTypes = ['client', 'vendor', 'partner', 'solution', 'governance'] as const;

  // ─── Render ───────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/45 via-slate-50 to-slate-100/90">
      {/* Header */}
      <header className="border-b-2 border-emerald-600/20 bg-gradient-to-b from-emerald-50/50 to-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="font-medium text-emerald-700 transition-colors hover:text-emerald-900"
            >
              Dashboard
            </button>
            <span className="text-emerald-300">/</span>
            <span className="font-semibold text-emerald-950">Upload Center</span>
          </nav>

          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-md ring-1 ring-emerald-800/10">
                <Upload size={24} aria-hidden />
              </div>
              <div className="min-w-0 border-l-2 border-emerald-500/35 pl-4 sm:pl-5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
                    Knowledge
                  </span>
                </div>
                <h1 className="mt-1 text-2xl font-bold tracking-tight text-emerald-950 sm:text-3xl">
                  Upload Center
                </h1>
                <p className="mt-1.5 max-w-2xl text-sm leading-snug text-emerald-900/70">
                  Select an entity, then add content via file upload, URL extraction, or domain crawl.
                </p>
              </div>
            </div>
            {documents.length > 0 && (
              <div className="hidden shrink-0 rounded-xl border border-emerald-200/70 bg-white/90 px-4 py-3 shadow-sm ring-1 ring-emerald-950/[0.04] sm:block">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700/80">
                  Total documents
                </div>
                <div className="font-mono text-2xl font-bold tabular-nums text-emerald-950">
                  {documents.length}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Governance entity info banner */}
      {selectedEntity?.type === 'governance' && (
        <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <div className="flex items-start gap-2">
              <Shield size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <div>
                <p className="font-semibold">Governance Document Upload</p>
                <p className="mt-1 text-amber-700">
                  Upload brand guidelines, compliance policies, or governance documents.
                  Emma will automatically extract structured rules (prohibited claims,
                  co-branding requirements, quality thresholds) into your Governance Dashboard.
                </p>
                <button
                  onClick={() => navigate('/governance')}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-900 underline hover:text-amber-700"
                >
                  View Governance Dashboard →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
        {/* ── STEP 1: Entity Picker (blocker) ── */}
        <div className="overflow-hidden rounded-2xl border border-emerald-200/60 bg-white shadow-sm ring-1 ring-emerald-950/[0.04]">
          <div className="h-1 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600" aria-hidden />
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white shadow-sm ring-1 ring-emerald-700/20">
                  {selectedEntity ? '✓' : '1'}
                </span>
                Select an Entity
              </h2>
              <p className="ml-9 mt-0.5 text-xs text-slate-500">
                {selectedEntity
                  ? `Uploading content for ${selectedEntity.name}`
                  : 'All content must be assigned to a client, vendor, partner, or solution'}
              </p>
            </div>
            {selectedEntity && (
              <button
                type="button"
                onClick={() => setSelectedEntity(null)}
                className="btn-emerald-outline rounded-xl px-3 py-1.5 text-sm font-medium"
              >
                Change
              </button>
            )}
          </div>

          <div className="p-6">
            {!selectedEntity &&
              (loadingEntities ? (
                <div className="py-10 text-center text-sm font-medium text-slate-500">
                  <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-emerald-600" aria-hidden />
                  Loading entities…
                </div>
              ) : entities.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 ring-1 ring-emerald-200/80">
                    <Building2 size={32} aria-hidden />
                  </div>
                  <p className="mb-4 text-sm text-slate-600">
                    No entities found. Create clients, vendors, or partners first.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/data-foundation')}
                    className="inline-flex items-center justify-center rounded-xl border border-emerald-600 bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg active:translate-y-px"
                  >
                    Go to Entity Manager
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
                  {entityTypes.map((type) => {
                    const items = entities.filter((e) => e.type === type);
                    if (items.length === 0) return null;
                    const styles = {
                      client: {
                        badge: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/60',
                        button:
                          'border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/80 hover:ring-1 hover:ring-emerald-200/50',
                      },
                      vendor: {
                        badge: 'bg-teal-100 text-teal-700 ring-1 ring-teal-200/60',
                        button:
                          'border-slate-200 hover:border-teal-400 hover:bg-teal-50/80 hover:ring-1 hover:ring-teal-200/50',
                      },
                      partner: {
                        badge: 'bg-cyan-100 text-cyan-800 ring-1 ring-cyan-200/60',
                        button:
                          'border-slate-200 hover:border-cyan-400 hover:bg-cyan-50/80 hover:ring-1 hover:ring-cyan-200/50',
                      },
                      solution: {
                        badge:
                          'bg-emerald-900/10 text-emerald-900 ring-1 ring-emerald-700/25',
                        button:
                          'border-slate-200 hover:border-emerald-600 hover:bg-emerald-50/90 hover:ring-1 hover:ring-emerald-300/50',
                      },
                      governance: {
                        badge: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200/60',
                        button:
                          'border-slate-200 hover:border-amber-400 hover:bg-amber-50/80 hover:ring-1 hover:ring-amber-200/50',
                      },
                    };
                    const typeStyles = styles[type as keyof typeof styles];

                    return (
                      <div key={type} className="space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`flex items-center justify-center w-6 h-6 rounded-md ${typeStyles.badge}`}>
                            {getEntityIcon(type)}
                          </span>
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                            {type}s
                          </span>
                          <span className="text-xs text-slate-400">({items.length})</span>
                        </div>
                        <div className="space-y-1.5">
                          {items.map((entity) => (
                            <button
                              key={entity.id}
                              type="button"
                              onClick={() => setSelectedEntity(entity)}
                              className={`flex w-full items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-3 py-2.5 text-left text-sm transition-all ${typeStyles.button} hover:shadow-sm`}
                            >
                              <span className="flex-1 truncate font-medium text-slate-900">
                                {entity.name}
                              </span>
                              {entity.parent && (
                                <span className="max-w-[80px] truncate text-xs text-slate-400">
                                  {entity.parent}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

            {selectedEntity && (
              <div className="mt-6 flex items-center gap-4 rounded-2xl border border-emerald-200/80 bg-gradient-to-r from-emerald-50/90 via-white to-teal-50/50 p-4 shadow-sm ring-1 ring-emerald-950/[0.04]">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-100">
                  {selectedEntity.icon ? (
                    <span className="text-2xl">{selectedEntity.icon}</span>
                  ) : (
                    React.cloneElement(getEntityIcon(selectedEntity.type) as React.ReactElement, { size: 24 })
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold text-slate-900">{selectedEntity.name}</div>
                  <div className="text-sm capitalize text-slate-600">
                    {selectedEntity.type}
                    {selectedEntity.parent ? ` · ${selectedEntity.parent}` : ''}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── STEP 2: Content Addition (only when entity selected) ── */}
        {selectedEntity && (
          <div className="overflow-hidden rounded-2xl border border-emerald-200/60 bg-white shadow-sm ring-1 ring-emerald-950/[0.04]">
            <div className="h-1 w-full bg-gradient-to-r from-teal-500 via-emerald-500 to-emerald-600" aria-hidden />
            <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
              <div className="mb-1 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white shadow-sm ring-1 ring-emerald-700/20">
                  2
                </span>
                <h2 className="text-base font-semibold text-slate-900">Add Content</h2>
              </div>
              <p className="ml-9 text-xs text-slate-500">
                Upload files or extract content from URLs for{' '}
                <strong className="font-semibold text-emerald-900">{selectedEntity.name}</strong>
              </p>
            </div>

            <div className="p-6">
              {/* Mode toggle */}
              <div className="mb-6 inline-flex gap-1 rounded-xl border border-slate-200/80 bg-slate-100/90 p-1 shadow-inner">
                <button
                  type="button"
                  onClick={() => setContentMode('upload')}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all ${contentMode === 'upload'
                      ? 'bg-white text-emerald-800 shadow-sm ring-1 ring-emerald-200/60'
                      : 'text-slate-600 hover:text-slate-900'
                    }`}
                >
                  📁 Upload Files
                </button>
                <button
                  type="button"
                  onClick={() => setContentMode('url')}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all ${contentMode === 'url'
                      ? 'bg-white text-emerald-800 shadow-sm ring-1 ring-emerald-200/60'
                      : 'text-slate-600 hover:text-slate-900'
                    }`}
                >
                  🌐 URL / Domain Crawl
                </button>
                <button
                  type="button"
                  onClick={() => setContentMode('paste')}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all ${contentMode === 'paste'
                      ? 'bg-white text-emerald-800 shadow-sm ring-1 ring-emerald-200/60'
                      : 'text-slate-600 hover:text-slate-900'
                    }`}
                >
                  📋 Paste Content
                </button>
              </div>

              {/* File upload mode — reuse DocumentUploadZone */}
              {contentMode === 'upload' && (
                <DocumentUploadZone
                  targetStep={3}
                  onExtractionComplete={handleExtractionComplete}
                  showPreview={true}
                  organizationId={orgId}
                  clientOrganizationId={entityFKProps.clientOrganizationId}
                  clientPartnerId={entityFKProps.clientPartnerId}
                  clientVendorId={entityFKProps.clientVendorId}
                  vendorSolutionId={entityFKProps.vendorSolutionId}
                  collectionType={
                    selectedEntity.type === 'vendor'
                      ? 'vendor'
                      : selectedEntity.type === 'partner'
                        ? 'partner'
                        : 'internal'
                  }
                  documentCategory={selectedEntity.type === 'governance' ? 'governance' : undefined}
                />
              )}

              {/* URL extraction mode — reuse PasteURLTab (single + domain crawl) */}
              {contentMode === 'url' && (
                <PasteURLTab
                  campaignId=""
                  organizationId={orgId}
                  onContentAdded={handleUrlContentAdded}
                  entityId={selectedEntity.id}
                  entityType={selectedEntity.type as 'client' | 'partner' | 'solution' | 'vendor'}
                  entityName={selectedEntity.name}
                />
              )}

              {/* Direct Text Paste mode */}
              {contentMode === 'paste' && (
                <PasteTextSubform
                  organizationId={orgId}
                  entityId={selectedEntity.id}
                  entityType={selectedEntity.type}
                  entityFKProps={entityFKProps}
                  onSuccess={handleUrlContentAdded}
                />
              )}
            </div>
          </div>
        )}

        {/* ── Disabled placeholder when no entity ── */}
        {!selectedEntity && entities.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-dashed border-emerald-200/70 bg-white/80 shadow-sm ring-1 ring-emerald-950/[0.03]">
            <div className="border-b border-slate-100 bg-slate-50/40 px-6 py-4">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-300 text-xs font-bold text-white">
                  2
                </span>
                <h2 className="text-base font-semibold text-slate-600">Add Content</h2>
              </div>
            </div>
            <div className="bg-gradient-to-b from-emerald-50/20 to-slate-50/30 p-12 text-center">
              <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-100 bg-emerald-50/50 text-emerald-400">
                <FolderOpen size={32} aria-hidden />
              </div>
              <p className="text-sm font-medium text-slate-600">
                Select an entity above to upload files or extract URLs
              </p>
            </div>
          </div>
        )}

        {/* ── STEP 3: Documents Table (always visible, grouped by entity) ── */}
        <div className="overflow-hidden rounded-2xl border border-emerald-200/60 bg-white shadow-sm ring-1 ring-emerald-950/[0.04]">
          <div className="h-1 w-full bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-500" aria-hidden />
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow-md ring-1 ring-emerald-800/10">
                <FileText size={18} aria-hidden />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Documents</h2>
                <p className="text-xs text-slate-500">
                  {documents.length} document{documents.length !== 1 ? 's' : ''} total
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={loadDocuments}
              className="btn-emerald-outline rounded-xl px-3 py-2 text-sm"
            >
              <RefreshCw size={14} aria-hidden /> Refresh
            </button>
          </div>

          {loadingDocs ? (
            <div className="p-12 text-center text-sm font-medium text-slate-500">
              <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-emerald-600" aria-hidden />
              Loading documents…
            </div>
          ) : documents.length === 0 ? (
            <div className="p-16 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-500 ring-1 ring-emerald-100">
                <FileText size={40} aria-hidden />
              </div>
              <p className="mb-1 font-semibold text-slate-800">No documents yet</p>
              <p className="text-sm text-slate-500">
                Select an entity and upload content above to get started
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {groupKeys.map((groupKey) => {
                const docs = grouped[groupKey];
                const firstDoc = docs[0];
                const groupEntity = firstDoc?.entity;
                const isGovernance = groupKey.startsWith('governance:') || firstDoc?.metadata?.document_category === 'governance';
                let labelText: string;
                let labelIcon: React.ReactNode;
                if (groupEntity) {
                  labelText = groupEntity.name;
                  labelIcon = getEntityIcon(groupEntity.type);
                } else if (isGovernance) {
                  labelText = 'Governance Documents';
                  labelIcon = <Shield size={16} className="text-amber-600" />;
                } else {
                  labelText = 'Unassigned';
                  labelIcon = <FileText size={16} />;
                }

                return (
                  <div key={groupKey}>
                    {/* Group header */}
                    <div className="border-b border-emerald-100/80 bg-gradient-to-r from-emerald-50/40 via-white to-teal-50/30 px-6 py-2.5">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                          {labelIcon}
                          {labelText}
                        </span>
                        <span className="rounded-full bg-emerald-100/80 px-2.5 py-0.5 text-xs font-semibold text-emerald-900">
                          {docs.length} file{docs.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    {/* Document rows */}
                    <table className="min-w-full">
                      <tbody className="divide-y divide-gray-100">
                        {docs.map((doc) => {
                          const ext = doc.name.split('.').pop()?.toUpperCase() || 'FILE';
                          return (
                            <tr key={doc.id} className="group transition-colors hover:bg-emerald-50/35">
                              <td className="px-6 py-5" style={{ maxWidth: '300px' }}>
                                <div className="flex items-center gap-3">
                                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-100 to-teal-100 text-xs font-bold text-emerald-900">
                                    {ext.substring(0, 4)}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div
                                      className="break-words text-sm font-medium leading-tight text-slate-900 transition-colors group-hover:text-emerald-800"
                                      style={{
                                        maxWidth: '220px',
                                        wordBreak: 'break-word',
                                        hyphens: 'auto',
                                        lineHeight: '1.3'
                                      }}
                                    >
                                      {doc.name}
                                    </div>
                                    <div className="text-xs text-slate-500">{formatSize(doc.size)}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-5 align-top">
                                <div className="flex justify-start">
                                  {statusBadge(doc.status)}
                                </div>
                              </td>
                              <td className="px-6 py-5 align-top">
                                <div className="flex items-start gap-2.5">
                                  {/* Subtle AI indicator - only for LLM-classified */}
                                  {doc.metadata?.source === 'openai_gpt4_mini' && (
                                    <span className="mt-0.5 shrink-0 text-emerald-600/70" title="AI-classified">
                                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                      </svg>
                                    </span>
                                  )}
                                  <p className="flex-1 text-sm leading-relaxed text-slate-700">
                                    {getRichDescription(doc)}
                                  </p>
                                </div>
                              </td>
                              <td className="w-44 px-6 py-5 align-top text-xs text-slate-500">
                                {formatDate(doc.uploadedAt)}
                              </td>
                              <td className="px-6 py-5 text-right align-top w-32">
                                <div className="flex items-center justify-end gap-1">
                                  {/* Mark as Template button */}
                                  {markingTemplate[doc.id] ? (
                                    <div
                                      className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center"
                                      title="Analyzing template structure..."
                                    >
                                      <Loader2 size={14} className="animate-spin text-amber-500" />
                                    </div>
                                  ) : doc.documentType === 'communication_template' ? (
                                    <button
                                      onClick={async () => {
                                        try {
                                          setMarkingTemplate(prev => ({ ...prev, [doc.id]: true }));
                                          await apiRequest(`/api/v1/documents/${doc.id}/mark-template`, {
                                            method: 'PATCH',
                                            body: JSON.stringify({ is_template: false }),
                                          });
                                          setDocuments(prev => prev.map(d =>
                                            d.id === doc.id ? { ...d, documentType: undefined } : d
                                          ));
                                        } catch (e) { console.error('Unmark template failed', e); }
                                        finally { setMarkingTemplate(prev => ({ ...prev, [doc.id]: false })); }
                                      }}
                                      className="w-8 h-8 rounded-lg text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition-all flex items-center justify-center border border-emerald-200"
                                      title="Template active — click to remove"
                                    >
                                      <span className="text-xs font-bold">T</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={async () => {
                                        try {
                                          setMarkingTemplate(prev => ({ ...prev, [doc.id]: true }));
                                          const res = await apiRequest(`/api/v1/documents/${doc.id}/mark-template`, {
                                            method: 'PATCH',
                                            body: JSON.stringify({ is_template: true, template_channel: 'any' }),
                                          });
                                          if (res.ok) {
                                            const data = await res.json();
                                            console.log('[Template] Analysis result:', data.template_analysis);
                                          }
                                          setDocuments(prev => prev.map(d =>
                                            d.id === doc.id ? { ...d, documentType: 'communication_template' } : d
                                          ));
                                        } catch (e) { console.error('Mark template failed', e); }
                                        finally { setMarkingTemplate(prev => ({ ...prev, [doc.id]: false })); }
                                      }}
                                      className="w-8 h-8 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all flex items-center justify-center border border-transparent hover:border-emerald-200"
                                      title="Use as template — AI will analyze structure"
                                    >
                                      <span className="text-xs">T</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteDocument(doc.id, doc.name)}
                                    className="w-8 h-8 rounded-lg text-gray-400 hover:text-white hover:bg-red-500 transition-all flex items-center justify-center border border-transparent hover:border-red-600 hover:shadow-sm"
                                    title="Delete document"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Navigation ── */}
        <div className="flex flex-col gap-4 pb-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => navigate('/data-foundation')}
            className="btn-emerald-outline rounded-xl px-4 py-2.5 text-sm"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to Entity Manager
          </button>

          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
            <span className="text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:text-left">
              Step 3 of 12
            </span>
            <button
              type="button"
              onClick={() => navigate('/workflow')}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg active:translate-y-px"
            >
              Continue to Campaign Setup
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface PasteTextSubformProps {
  organizationId: string;
  entityId: string;
  entityType: string;
  entityFKProps: Record<string, string | undefined>;
  onSuccess: (docId: string, title: string, preview: string) => void;
}

const PasteTextSubform: React.FC<PasteTextSubformProps> = ({
  organizationId,
  entityId,
  entityType,
  entityFKProps,
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    setSubmitting(true);
    setMessage(null);

    try {
      let rawTitle = title.trim() || `pasted_content_${new Date().toISOString().slice(0, 10)}`;
      if (!rawTitle.toLowerCase().endsWith('.txt')) {
        rawTitle += '.txt';
      }
      const docName = rawTitle;
      const blob = new Blob([content], { type: 'text/plain' });
      const file = new File([blob], docName, { type: 'text/plain' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('target_step', '3');
      formData.append('organization_id', organizationId);
      if (entityFKProps.clientOrganizationId) formData.append('client_organization_id', entityFKProps.clientOrganizationId);
      if (entityFKProps.clientPartnerId) formData.append('client_partner_id', entityFKProps.clientPartnerId);
      if (entityFKProps.clientVendorId) formData.append('client_vendor_id', entityFKProps.clientVendorId);
      if (entityFKProps.vendorSolutionId) formData.append('vendor_solution_id', entityFKProps.vendorSolutionId);

      const response = await apiRequest('/api/v1/documents/ingest', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail?.message || errorData.detail || 'Failed to ingest pasted text');
      }

      const data = await response.json();
      setMessage('✅ Text content successfully ingested!');
      setTitle('');
      setContent('');
      onSuccess(data.document_id || '', docName, content.slice(0, 300));
    } catch (err) {
      console.error('Paste text ingest failed:', err);
      setMessage(`❌ ${err instanceof Error ? err.message : 'Text ingestion failed'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/50 p-5">
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase text-slate-600">Document Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Q3 Partnership Overview & Product Notes"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-emerald-500 focus:outline-none"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold uppercase text-slate-600">Raw Content Text</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={8}
          required
          placeholder="Paste strategic notes, solution briefs, case studies, or raw intelligence content here..."
          className="w-full rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-800 placeholder-slate-400 focus:border-emerald-500 focus:outline-none"
        />
      </div>

      {message && (
        <div className={`p-3 text-xs font-semibold rounded-lg ${message.startsWith('✅') ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {message}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting || !content.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow transition-all hover:bg-emerald-700 disabled:opacity-50"
        >
          {submitting ? 'Ingesting Content...' : 'Ingest Pasted Content'}
        </button>
      </div>
    </form>
  );
};
